import { useEffect, useEffectEvent, useRef, type RefObject } from 'react';

import * as Sentry from '@sentry/react';

import {
  isPlayBlockedByBrowser,
  resolveMediaErrorCause,
  resolveMediaIdentity,
  shouldReportMediaError,
} from './ui.video-player.helpers';

export function usePlaybackSync(
  playerRef: RefObject<HTMLVideoElement | null>,
  isPlaying: boolean,
  onPlayBlocked: () => void
) {
  const handlePlayBlocked = useEffectEvent(onPlayBlocked);

  /* oxlint-disable react-you-might-not-need-an-effect/no-event-handler -- syncs React play state onto the external HTMLMediaElement; no dependency array because react-player mounts the element lazily and a dep-gated effect would miss the commit where the ref first resolves */
  useEffect(() => {
    const media = playerRef.current;
    if (!media) return;

    if (isPlaying && media.paused) {
      Promise.resolve(media.play()).catch((error: unknown) => {
        if (isPlayBlockedByBrowser(error)) handlePlayBlocked();
      });
      return;
    }

    if (!isPlaying && !media.paused) media.pause();
  });
  /* oxlint-enable react-you-might-not-need-an-effect/no-event-handler */
}

export function useLoadFailureReport(src: string, hasEverPainted: boolean) {
  const reportedMediaRef = useRef<string | null>(null);

  return (mediaError: Pick<MediaError, 'code' | 'message'> | null) => {
    const mediaPath = resolveMediaIdentity(src);
    if (!shouldReportMediaError(mediaError?.code)) return;
    if (reportedMediaRef.current === mediaPath) return;

    reportedMediaRef.current = mediaPath;
    const cause = resolveMediaErrorCause(hasEverPainted);
    const code = String(mediaError?.code ?? 'unknown');

    Sentry.captureMessage('Video failed to load', {
      level: 'warning',
      fingerprint: ['video-player-load-failure', cause, code],
      tags: { 'video.cause': cause, 'video.media_error_code': code },
      extra: { mediaPath, mediaErrorMessage: mediaError?.message ?? null },
    });
  };
}
